import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@apollo/client'
import SimpleHeader from '../layouts/SimpleHeader'
import SideMenu from '../layouts/SideMenu'
import MenuWrap from '../components/BurgerMenu/MenuWrap'
import LoadingPage from './LoadingPage'
import { ADMIN_CVES_QUERY } from '../services/GraphQLQueries'

const formatDateTime = (value) => {
  if (!value) {
    return '-'
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return '-'
  }

  return parsed.toLocaleString()
}

const formatShortId = (value) => {
  if (!value) {
    return '-'
  }

  return `${value.slice(0, 8)}...`
}

const getCveAnchorId = (id) => `cve-${id}`

const getCveLink = (id) => `/admin-cves#${getCveAnchorId(id)}`

const getStatusClasses = (status) => {
  switch (status) {
    case 'Verified':
      return 'bg-[#e8f5eb] text-[#255a38]'
    case 'Rejected':
      return 'bg-[#fdecec] text-[#9d3434]'
    case 'Duplicate':
      return 'bg-[#fef4da] text-[#8a5a00]'
    case 'Flagged':
      return 'bg-[#fff0e3] text-[#9a4c00]'
    case 'Unmatched':
      return 'bg-[#edf2ff] text-[#334fa3]'
    default:
      return 'bg-[#eef2f0] text-[#54635a]'
  }
}

const AdminCves = () => {
  const [search, setSearch] = useState('')
  const [highlightedCveId, setHighlightedCveId] = useState(null)
  const [pendingScrollCveId, setPendingScrollCveId] = useState(null)
  const highlightTimeoutRef = useRef(null)
  const handledInitialHashRef = useRef(false)
  const {
    data: cveData,
    loading: cveLoading,
    error: cveError,
  } = useQuery(ADMIN_CVES_QUERY, {
    fetchPolicy: 'no-cache',
  })

  const cves = useMemo(() => cveData?.adminCves ?? [], [cveData])
  const normalizedSearch = search.trim().toLowerCase()

  const filteredCves = useMemo(() => {
    if (!normalizedSearch) {
      return cves
    }

    return cves.filter((cve) =>
      [
        cve.status,
        cve.siteName,
        cve.domain,
        cve.campaignName,
        cve.source,
        cve.externalConversionId,
        cve.externalSubmissionId,
        cve.contractTierName,
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(normalizedSearch))
    )
  }, [cves, normalizedSearch])

  const summary = useMemo(
    () => ({
      total: cves.length,
      counted: cves.filter((cve) => cve.countsTowardCve).length,
      rejected: cves.filter((cve) => cve.status === 'Rejected').length,
      duplicates: cves.filter((cve) => cve.status === 'Duplicate').length,
    }),
    [cves]
  )

  const scrollToCve = useCallback((cveId) => {
    const targets = Array.from(document.querySelectorAll(`[data-cve-anchor="${getCveAnchorId(cveId)}"]`))
    const target = targets.find((element) => element.getClientRects().length > 0)

    if (!target) {
      return false
    }

    target.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setHighlightedCveId(cveId)

    if (highlightTimeoutRef.current) {
      window.clearTimeout(highlightTimeoutRef.current)
    }

    highlightTimeoutRef.current = window.setTimeout(() => {
      setHighlightedCveId(null)
      highlightTimeoutRef.current = null
    }, 2400)

    return true
  }, [])

  const handleDuplicateClick = (event, duplicateOfEventId) => {
    event.preventDefault()
    window.history.pushState(null, '', getCveLink(duplicateOfEventId))

    if (!scrollToCve(duplicateOfEventId)) {
      setSearch('')
      setPendingScrollCveId(duplicateOfEventId)
    }
  }

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        window.clearTimeout(highlightTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!pendingScrollCveId) {
      return
    }

    if (scrollToCve(pendingScrollCveId)) {
      setPendingScrollCveId(null)
    }
  }, [filteredCves, pendingScrollCveId, scrollToCve])

  useEffect(() => {
    if (handledInitialHashRef.current || cveLoading || cves.length === 0) {
      return
    }

    const hashPrefix = '#cve-'
    if (window.location.hash.startsWith(hashPrefix)) {
      const cveId = window.location.hash.slice(hashPrefix.length)
      setPendingScrollCveId(cveId)
    }

    handledInitialHashRef.current = true
  }, [cveLoading, cves.length])

  if (cveLoading) {
    return <LoadingPage />
  }

  return (
    <div id="outer-container" className="min-h-screen">
      <MenuWrap className="sm:hidden">
        <SideMenu isAuthorized={true}></SideMenu>
      </MenuWrap>
      <div id="page-wrap" className="min-h-screen">
        <div className="flex min-h-screen items-stretch">
          <div className="sticky top-0 hidden h-screen shrink-0 sm:block">
            <SideMenu isAuthorized={true}></SideMenu>
          </div>
          <div className="w-full min-w-0">
            <SimpleHeader />
            <div className="min-h-[calc(100vh-175px)] bg-gradient-to-br from-white via-[#f7fbf8] to-[#edf4ef] px-6 py-12">
              <div className="mx-auto max-w-7xl space-y-8">
                <div className="rounded-3xl border border-[#dbe7df] bg-white/90 p-8 shadow-xl">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-[#6f8a79]">
                        Organization reporting
                      </p>
                      <h1 className="mt-2 text-3xl font-semibold text-[#1f2b23]">
                        Conversion verification events
                      </h1>
                      <p className="mt-3 text-sm text-[#6d7b73]">
                        Review every CVE recorded for your organization, including status, source, site, and linked
                        campaign context.
                      </p>
                    </div>
                    <div className="w-full max-w-md">
                      <label className="block text-sm font-medium text-[#1f2b23]" htmlFor="cve-search">
                        Search CVEs
                      </label>
                      <input
                        id="cve-search"
                        type="text"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Search by site, status, campaign, source..."
                        className="mt-2 w-full rounded-2xl border border-[#dbe7df] bg-[#f6faf7] px-4 py-3 text-base text-[#1f2b23] shadow-sm focus:border-[#86b194] focus:outline-none focus:ring-2 focus:ring-[#c7e0cf]"
                      />
                    </div>
                  </div>

                  <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <div className="rounded-2xl border border-[#dbe7df] bg-[#f6faf7] p-5">
                      <p className="text-xs uppercase tracking-[0.18em] text-[#6f8a79]">Total CVEs</p>
                      <p className="mt-3 text-3xl font-semibold text-[#1f2b23]">{summary.total}</p>
                    </div>
                    <div className="rounded-2xl border border-[#dbe7df] bg-[#f6faf7] p-5">
                      <p className="text-xs uppercase tracking-[0.18em] text-[#6f8a79]">Counted Toward CVE</p>
                      <p className="mt-3 text-3xl font-semibold text-[#1f2b23]">{summary.counted}</p>
                    </div>
                    <div className="rounded-2xl border border-[#dbe7df] bg-[#f6faf7] p-5">
                      <p className="text-xs uppercase tracking-[0.18em] text-[#6f8a79]">Rejected</p>
                      <p className="mt-3 text-3xl font-semibold text-[#1f2b23]">{summary.rejected}</p>
                    </div>
                    <div className="rounded-2xl border border-[#dbe7df] bg-[#f6faf7] p-5">
                      <p className="text-xs uppercase tracking-[0.18em] text-[#6f8a79]">Duplicates</p>
                      <p className="mt-3 text-3xl font-semibold text-[#1f2b23]">{summary.duplicates}</p>
                    </div>
                  </div>

                  {cveError ? (
                    <div className="mt-6 rounded-2xl border border-[#f0c5c5] bg-[#fff5f5] px-5 py-4 text-sm text-[#8a3d3d]">
                      {cveError.message || 'We could not load CVEs right now.'}
                    </div>
                  ) : null}

                  {!cveError && filteredCves.length === 0 ? (
                    <div className="mt-6 rounded-2xl border border-dashed border-[#dbe7df] bg-[#fbfdfb] px-6 py-12 text-center">
                      <p className="text-lg font-semibold text-[#1f2b23]">
                        {cves.length === 0 ? 'No CVEs have been recorded yet.' : 'No CVEs match your search.'}
                      </p>
                      <p className="mt-2 text-sm text-[#6d7b73]">
                        {cves.length === 0
                          ? 'Once conversion verification events are saved, they will appear here.'
                          : 'Try a different site, status, campaign, or source value.'}
                      </p>
                    </div>
                  ) : null}

                  {filteredCves.length > 0 ? (
                    <>
                      <div className="mt-6 space-y-4 lg:hidden">
                        {filteredCves.map((cve) => (
                          <div
                            data-cve-anchor={getCveAnchorId(cve.id)}
                            key={cve.id}
                            className={`scroll-mt-24 rounded-2xl border border-[#dbe7df] p-5 shadow-sm transition-colors duration-500 ${
                              highlightedCveId === cve.id ? 'bg-[#fff5c2]' : 'bg-[#fbfdfb]'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-sm font-semibold text-[#1f2b23]">
                                  {cve.siteName || cve.domain || 'Unknown site'}
                                </p>
                                <p className="mt-1 text-xs text-[#6d7b73]">{formatDateTime(cve.submittedAtUtc)}</p>
                              </div>
                              <span
                                className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusClasses(cve.status)}`}
                              >
                                {cve.status || 'Unknown'}
                              </span>
                            </div>
                            <div className="mt-4 grid gap-3 text-sm text-[#405147]">
                              <p>Campaign: {cve.campaignName || formatShortId(cve.trackingCampaignId)}</p>
                              <p>Source: {cve.source || '-'}</p>
                              <p>External conversion: {cve.externalConversionId || '-'}</p>
                              <p>Counts toward CVE: {cve.countsTowardCve ? 'Yes' : 'No'}</p>
                              <p>Contract tier: {cve.contractTierName || '-'}</p>
                              <p>Rejection reason: {cve.rejectionReason || '-'}</p>
                              {cve.duplicateOfEventId ? (
                                <p>
                                  Duplicate of:{' '}
                                  <a
                                    href={getCveLink(cve.duplicateOfEventId)}
                                    title={cve.duplicateOfEventId}
                                    onClick={(event) => handleDuplicateClick(event, cve.duplicateOfEventId)}
                                    className="font-medium text-[#1f2b23] underline decoration-[#9fb6aa] underline-offset-2 hover:text-[#168A4A]"
                                  >
                                    {formatShortId(cve.duplicateOfEventId)}
                                  </a>
                                </p>
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="mt-6 hidden overflow-hidden rounded-3xl border border-[#dbe7df] lg:block">
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-[#dbe7df] bg-white">
                            <thead className="bg-[#f5faf7] text-left text-xs uppercase tracking-[0.18em] text-[#6f8a79]">
                              <tr>
                                <th className="px-5 py-4">Submitted</th>
                                <th className="px-5 py-4">Site</th>
                                <th className="px-5 py-4">Campaign</th>
                                <th className="px-5 py-4">Status</th>
                                <th className="px-5 py-4">Counts</th>
                                <th className="px-5 py-4">Source</th>
                                <th className="px-5 py-4">External Ids</th>
                                <th className="px-5 py-4">Details</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-[#edf3ee] text-sm text-[#405147]">
                              {filteredCves.map((cve) => (
                                <tr
                                  data-cve-anchor={getCveAnchorId(cve.id)}
                                  key={cve.id}
                                  className={`scroll-mt-24 align-top transition-colors duration-500 ${
                                    highlightedCveId === cve.id ? 'bg-[#fff5c2]' : 'bg-white'
                                  }`}
                                >
                                  <td className="px-5 py-4">
                                    <p className="font-medium text-[#1f2b23]">{formatDateTime(cve.submittedAtUtc)}</p>
                                    <p className="mt-1 text-xs text-[#6d7b73]">
                                      Counted: {formatDateTime(cve.countedAtUtc)}
                                    </p>
                                  </td>
                                  <td className="px-5 py-4">
                                    <p className="font-medium text-[#1f2b23]">{cve.siteName || 'Unknown site'}</p>
                                    <p className="mt-1 text-xs text-[#6d7b73]">{cve.domain || '-'}</p>
                                    <p className="mt-1 text-xs text-[#6d7b73]">Tracking ID: {cve.trackingId || '-'}</p>
                                  </td>
                                  <td className="px-5 py-4">
                                    <p className="font-medium text-[#1f2b23]">
                                      {cve.campaignName || formatShortId(cve.trackingCampaignId)}
                                    </p>
                                    <p className="mt-1 text-xs text-[#6d7b73]">Tracker: {formatShortId(cve.trackerId)}</p>
                                    <p className="mt-1 text-xs text-[#6d7b73]">Click: {formatShortId(cve.trackerClickId)}</p>
                                  </td>
                                  <td className="px-5 py-4">
                                    <span
                                      className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusClasses(cve.status)}`}
                                    >
                                      {cve.status || 'Unknown'}
                                    </span>
                                    <p className="mt-3 text-xs text-[#6d7b73]">
                                      Original event: {formatDateTime(cve.originalEventTimestampUtc)}
                                    </p>
                                  </td>
                                  <td className="px-5 py-4">
                                    <p className="font-medium text-[#1f2b23]">
                                      {cve.countsTowardCve ? 'Counts toward CVE' : 'Not counted'}
                                    </p>
                                    <p className="mt-1 text-xs text-[#6d7b73]">
                                      Contract tier: {cve.contractTierName || '-'}
                                    </p>
                                  </td>
                                  <td className="px-5 py-4">
                                    <p>{cve.source || '-'}</p>
                                  </td>
                                  <td className="px-5 py-4">
                                    <p className="font-medium text-[#1f2b23]">
                                      {cve.externalConversionId || '-'}
                                    </p>
                                    <p className="mt-1 text-xs text-[#6d7b73]">
                                      Submission: {cve.externalSubmissionId || '-'}
                                    </p>
                                  </td>
                                  <td className="px-5 py-4">
                                    <p>{cve.rejectionReason || '-'}</p>
                                    {cve.duplicateOfEventId ? (
                                      <p className="mt-1 text-xs text-[#6d7b73]">
                                        Duplicate of:{' '}
                                        <a
                                          href={getCveLink(cve.duplicateOfEventId)}
                                          title={cve.duplicateOfEventId}
                                          onClick={(event) => handleDuplicateClick(event, cve.duplicateOfEventId)}
                                          className="font-medium text-[#1f2b23] underline decoration-[#9fb6aa] underline-offset-2 hover:text-[#168A4A]"
                                        >
                                          {formatShortId(cve.duplicateOfEventId)}
                                        </a>
                                      </p>
                                    ) : null}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminCves
